'use client';

/**
 * Shared modal shell for the onboarding wizards (SetupWizardModal,
 * BrandValuesWizardModal): backdrop, panel, step label, progress segments
 * and close button.
 *
 * - Desktop (sm+): centered panel, max 560px wide.
 * - Mobile: full-screen sheet.
 * - Backdrop click / Escape do NOT close (deliberate choices only) — closing
 *   is session-only dismissal via the explicit close button.
 */

import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';

interface WizardShellProps {
  isOpen: boolean;
  /** Session-only dismissal (explicit close button) */
  onClose: () => void;
  /** Small uppercase label above the progress segments */
  stepLabel: string;
  /** Total number of progress segments */
  segmentsTotal: number;
  /** Number of filled segments (0..segmentsTotal) */
  segmentsFilled: number;
  children: React.ReactNode;
}

export default function WizardShell({
  isOpen,
  onClose,
  stepLabel,
  segmentsTotal,
  segmentsFilled,
  children,
}: WizardShellProps) {
  const { darkMode } = useDarkMode();

  return (
    <Dialog open={isOpen} onClose={() => {}} className="relative z-[150]">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity duration-300 ease-out data-closed:opacity-0"
      />

      <div className="fixed inset-0 z-[150] w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center sm:p-4">
          <DialogPanel
            transition
            className={`relative flex min-h-dvh w-full transform flex-col overflow-hidden text-left shadow-2xl transition-all duration-300 ease-out data-closed:scale-95 data-closed:opacity-0 sm:min-h-0 sm:max-w-[560px] sm:rounded-2xl ${
              darkMode ? 'border-white/10 bg-gray-800 sm:border' : 'bg-white'
            }`}
          >
            {/* Step label + close + progress segments */}
            <div className="px-6 pt-5">
              <div className="mb-1.5 flex items-center justify-between">
                <span
                  className={`text-[11px] font-semibold tracking-wide uppercase ${
                    darkMode ? 'text-indigo-400' : 'text-indigo-600'
                  }`}
                >
                  {stepLabel}
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Close"
                  className={`rounded-md p-1 transition-colors ${
                    darkMode
                      ? 'text-gray-500 hover:bg-gray-700 hover:text-gray-300'
                      : 'text-gray-300 hover:bg-gray-100 hover:text-gray-500'
                  }`}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="flex gap-1.5">
                {Array.from({ length: segmentsTotal }, (_, i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full ${
                      i < segmentsFilled
                        ? 'bg-indigo-600'
                        : darkMode
                          ? 'bg-gray-700'
                          : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            </div>

            <div className="flex-1 px-6 pt-5 pb-6">{children}</div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
